import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  type ToggleButtonProps,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ApiError } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  markTestResult,
  type ReleaseNote,
  type TestMark,
  type TestMarkStatus,
  type TestResults,
} from '@/api/releaseApproval';
import { groupChanges } from './groupChanges';
import { changeStatus, displayName, summarize, testResultsKey, useTestResults } from './testResults';

const CATEGORY_ORDER = ['new', 'improved', 'fixed'] as const;

interface MarkInput {
  noteId: string;
  status: TestMarkStatus;
  comment?: string;
}

/**
 * The text a mark carries: a not-ok mark's description (required before it is
 * sent), a not-tested mark's reason (#3750) and an ok mark's comment (#3816).
 * Ok and not tested save on one click; their text is added afterwards.
 */
const COMMENT_DIALOG = {
  ok: {
    title: 'checklist.okTitle',
    label: 'checklist.okComment',
    help: 'checklist.okCommentHelp',
    confirm: 'checklist.okConfirm',
    color: 'success',
    required: false,
    testId: 'ok-comment',
  },
  not_ok: {
    title: 'checklist.notOkTitle',
    label: 'checklist.notOkComment',
    help: 'checklist.notOkCommentHelp',
    confirm: 'checklist.notOkConfirm',
    color: 'error',
    required: true,
    testId: 'not-ok-comment',
  },
  not_tested: {
    title: 'checklist.notTestedTitle',
    label: 'checklist.notTestedComment',
    help: 'checklist.notTestedCommentHelp',
    confirm: 'checklist.notTestedConfirm',
    color: 'warning',
    required: false,
    testId: 'not-tested-comment',
  },
} as const;

const MARK_CHIP = {
  ok: { color: 'success', icon: <CheckIcon /> },
  not_ok: { color: 'error', icon: <CloseIcon /> },
  not_tested: { color: 'warning', icon: <RemoveCircleOutlineIcon /> },
} as const;

/** The tester's buttons, in the order they sit on the row (#3491, #3750). */
const MARK_BUTTONS = [
  { status: 'ok', labelKey: 'checklist.ok', color: 'success', icon: <CheckIcon fontSize="small" /> },
  { status: 'not_ok', labelKey: 'checklist.notOk', color: 'error', icon: <CloseIcon fontSize="small" /> },
  {
    status: 'not_tested',
    labelKey: 'checklist.notTested',
    color: 'warning',
    icon: <RemoveCircleOutlineIcon fontSize="small" />,
  },
] as const satisfies readonly { status: TestMarkStatus; labelKey: string; color: ToggleButtonProps['color']; icon: ReactNode }[];

/**
 * A mark button: an icon with the label in a tooltip and on the accessible
 * name (#3825), because three labelled buttons took a third of the row from the
 * change itself. On a phone the group has a row of its own and a tooltip has no
 * hover, so the label is shown next to the icon there (#3828). `disabled` comes
 * as a prop, not from the group, so the tooltip can go quiet on a disabled
 * button (MUI warns about one otherwise).
 */
function MarkButton({ label, children, ...props }: ToggleButtonProps & { label: string }) {
  return (
    <Tooltip title={props.disabled ? '' : label}>
      <ToggleButton aria-label={label} {...props}>
        {children}
        <Box component="span" aria-hidden sx={{ display: { xs: 'inline', sm: 'none' }, ml: 0.5 }}>
          {label}
        </Box>
      </ToggleButton>
    </Tooltip>
  );
}

/**
 * The issues and PR behind a change, linked to GitHub when staff-api names the
 * repository (#4197), so a tester's finding can be traced to the work.
 */
function ChangeRefs({ note, repoUrl }: { note: ReleaseNote; repoUrl: string | null }) {
  const { t } = useTranslation('releaseApproval');
  const refs = [
    ...(note.issues ?? []).map((number) => ({
      key: `issue-${number}`,
      label: t('checklist.issueRef', { number }),
      aria: t('checklist.openIssue', { number }),
      path: `issues/${number}`,
    })),
    ...(note.pr === undefined
      ? []
      : [
          {
            key: `pr-${note.pr}`,
            label: t('checklist.prRef', { number: note.pr }),
            aria: t('checklist.openPr', { number: note.pr }),
            path: `pull/${note.pr}`,
          },
        ]),
  ];
  if (refs.length === 0) return null;
  return (
    <Box component="span" data-testid={`change-refs-${note.id}`} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1 }}>
      {refs.map((ref) =>
        repoUrl ? (
          <Link
            key={ref.key}
            href={`${repoUrl}/${ref.path}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ref.aria}
            variant="caption"
          >
            {ref.label}
          </Link>
        ) : (
          <Typography key={ref.key} component="span" variant="caption">
            {ref.label}
          </Typography>
        ),
      )}
    </Box>
  );
}

function CommentDialog({
  status,
  title,
  initialComment,
  submitting,
  onClose,
  onConfirm,
}: {
  status: TestMarkStatus;
  title: string;
  /** The tester's saved text when they edit an existing mark; the dialog then saves instead of marking. */
  initialComment: string | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}) {
  const { t } = useTranslation('releaseApproval');
  const { t: tc } = useTranslation('common');
  const [comment, setComment] = useState(initialComment ?? '');
  const trimmed = comment.trim();
  const config = COMMENT_DIALOG[status];
  return (
    <ResponsiveDialog open onClose={submitting ? undefined : onClose} mobileTitle={t(config.title)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>{t(config.title)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography fontWeight={600}>{title}</Typography>
          <TextField
            required={config.required}
            label={t(config.label)}
            helperText={t(config.help)}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            multiline
            minRows={3}
            inputProps={{ maxLength: 1000, 'data-testid': config.testId }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          color={config.color}
          disabled={submitting || (config.required && trimmed === '')}
          onClick={() => {
            onConfirm(trimmed);
          }}
        >
          {initialComment === null ? t(config.confirm) : tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

function OthersMarks({ marks }: { marks: readonly TestMark[] }) {
  const { t } = useTranslation('releaseApproval');
  if (marks.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {marks.map((m) => (
        <Tooltip key={`${m.userId}-${m.fromSha ?? ''}`} title={m.comment ?? ''} disableHoverListener={!m.comment}>
          <Chip
            size="small"
            variant="outlined"
            color={MARK_CHIP[m.status].color}
            icon={MARK_CHIP[m.status].icon}
            data-testid={m.inherited ? `inherited-mark-${m.noteId}` : undefined}
            // An earlier version's mark (#3547): dashed, with the version.
            sx={m.inherited ? { borderStyle: 'dashed' } : undefined}
            label={
              m.inherited
                ? t('checklist.inheritedMark', {
                    name: displayName(m.by),
                    version: m.fromVersion ?? m.fromSha?.slice(0, 7) ?? '',
                  })
                : displayName(m.by)
            }
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/**
 * The changes against production, one row each, with the tester's ok / not ok
 * / not tested at the end of the row (#3491, #3750). Not ok asks for a
 * description first; ok and not tested save on one click and the tester can
 * add a comment afterwards (#3816). Marks are shared through GitHub, so every
 * tester and the approver see who marked what.
 */
export function ChangesChecklist({
  notes,
  loading,
  sha,
  language,
  repoUrl = null,
}: {
  notes: readonly ReleaseNote[];
  loading: boolean;
  sha: string | null;
  language: 'fi' | 'en';
  /** GitHub repository the change's issue and PR numbers link to (#4197). */
  repoUrl?: string | null;
}) {
  const { t } = useTranslation('releaseApproval');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const results = useTestResults(sha);
  const [commentFor, setCommentFor] = useState<{
    note: ReleaseNote;
    status: TestMarkStatus;
    initialComment: string | null;
  } | null>(null);

  const key = testResultsKey(sha ?? '');
  const mark = useMutation({
    mutationFn: (input: MarkInput) => markTestResult({ sha: sha ?? '', ...input }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TestResults>(key);
      if (previous && userId) {
        const optimistic: TestMark = {
          sha: sha ?? '',
          noteId: input.noteId,
          status: input.status,
          comment: input.comment ?? null,
          userId,
          by: previous.marks.find((m) => m.userId === userId)?.by ?? '',
          at: new Date().toISOString(),
        };
        queryClient.setQueryData<TestResults>(key, {
          ...previous,
          marks: [...previous.marks.filter((m) => !(m.noteId === input.noteId && m.userId === userId)), optimistic],
        });
      }
      return { previous };
    },
    onSuccess: () => {
      setCommentFor(null);
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      const code =
        error instanceof ApiError && typeof error.data === 'object' && error.data !== null
          ? (error.data as { code?: string }).code
          : undefined;
      notify.error(
        t(
          code === 'stale_candidate'
            ? 'checklist.errors.staleCandidate'
            : code === 'github_permission_missing'
              ? 'checklist.errors.permissionMissing'
              : 'checklist.errors.saveFailed'
        )
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const marks = results.data?.marks ?? [];
  const markingDisabled = !sha || !results.data?.configured || mark.isPending;
  const summary = summarize(notes, marks);
  const grouped = groupChanges(notes);
  const githubError = results.data?.githubError ?? null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('changesTitle')}
        </Typography>
        {loading && <CircularProgress size={20} />}
        {!loading && notes.length === 0 && <Typography color="text.secondary">{t('noChanges')}</Typography>}
        {notes.length > 0 && (
          <Stack spacing={1} sx={{ mb: 1 }}>
            <Typography data-testid="checklist-summary">
              {t('checklist.summary', {
                count: summary.total,
                ok: summary.ok,
                notOk: summary.notOk,
                notTested: summary.notTested,
                untested: summary.untested,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('checklist.onlyNotes')}
              {results.data?.issueUrl && (
                <>
                  {' '}
                  <Link href={results.data.issueUrl} target="_blank" rel="noopener noreferrer">
                    {t('checklist.issueLink')}
                  </Link>
                </>
              )}
            </Typography>
            {githubError?.kind === 'permission_missing' && (
              <Alert severity="error" data-testid="checklist-permission-missing">
                {t('checklist.errors.permissionMissing')}
              </Alert>
            )}
            {githubError?.kind === 'unavailable' && <Alert severity="warning">{t('checklist.errors.unavailable')}</Alert>}
            {results.data && !results.data.configured && <Alert severity="info">{t('checklist.notConfigured')}</Alert>}
          </Stack>
        )}
        {CATEGORY_ORDER.filter((category) => grouped[category].length > 0).map((category) => (
          <Box key={category} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t(`category.${category}`)}
            </Typography>
            <List disablePadding>
              {grouped[category].map((note, index) => {
                const noteMarks = marks.filter((m) => m.noteId === note.id);
                // The toggle shows only what the tester confirmed on this
                // version; their own earlier-version mark shows as a chip
                // with the version (#3547), like everyone else's.
                const mine = noteMarks.find((m) => m.userId === userId && !m.inherited) ?? null;
                const others = noteMarks.filter((m) => m !== mine);
                const status = changeStatus(note.id, marks);
                return (
                  <Fragment key={note.id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      disableGutters
                      data-testid={`change-row-${note.id}`}
                      data-status={status}
                      sx={{ py: 1.5, gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                    >
                      <ListItemText
                        // On a phone the text takes the whole row and the marks wrap below it (#3828).
                        sx={{ m: 0, flex: 1, minWidth: 0, flexBasis: { xs: '100%', sm: 0 } }}
                        primary={note[language].title}
                        secondary={
                          <Stack spacing={0.5} component="span" sx={{ display: 'flex' }}>
                            {(note.actionRequired || note[language].body) && (
                              <span>{note.actionRequired ? t('actionRequired') : note[language].body}</span>
                            )}
                            <ChangeRefs note={note} repoUrl={repoUrl} />
                            {mine?.status === 'ok' && mine.comment && (
                              <span>{t('checklist.yourOkComment', { comment: mine.comment })}</span>
                            )}
                            {mine?.status === 'not_ok' && mine.comment && (
                              <span>{t('checklist.yourComment', { comment: mine.comment })}</span>
                            )}
                            {mine?.status === 'not_tested' && mine.comment && (
                              <span>{t('checklist.yourReason', { comment: mine.comment })}</span>
                            )}
                            {mine && (
                              <span>
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={mark.isPending}
                                  data-testid={`edit-comment-${note.id}`}
                                  sx={{ px: 0, minWidth: 0 }}
                                  onClick={() => {
                                    setCommentFor({ note, status: mine.status, initialComment: mine.comment ?? '' });
                                  }}
                                >
                                  {t(mine.comment ? 'checklist.editComment' : 'checklist.addComment')}
                                </Button>
                              </span>
                            )}
                            <OthersMarks marks={others} />
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mine?.status ?? null}
                        disabled={markingDisabled}
                        aria-label={t('checklist.markLabel', { title: note[language].title })}
                        sx={{
                          flexShrink: 0,
                          width: { xs: '100%', sm: 'auto' },
                          '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' } },
                        }}
                        onChange={(_event, value: TestMarkStatus | null) => {
                          if (value === 'not_ok') setCommentFor({ note, status: value, initialComment: null });
                          else if (value) mark.mutate({ noteId: note.id, status: value });
                        }}
                      >
                        {MARK_BUTTONS.map((button) => (
                          <MarkButton
                            key={button.status}
                            value={button.status}
                            color={button.color}
                            disabled={markingDisabled}
                            label={t(button.labelKey)}
                            data-testid={`mark-${button.status.replace('_', '-')}-${note.id}`}
                          >
                            {button.icon}
                          </MarkButton>
                        ))}
                      </ToggleButtonGroup>
                    </ListItem>
                  </Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </CardContent>
      {commentFor && (
        <CommentDialog
          key={`${commentFor.note.id}-${commentFor.status}`}
          status={commentFor.status}
          title={commentFor.note[language].title}
          initialComment={commentFor.initialComment}
          submitting={mark.isPending}
          onClose={() => {
            setCommentFor(null);
          }}
          onConfirm={(comment) => {
            mark.mutate({
              noteId: commentFor.note.id,
              status: commentFor.status,
              ...(comment === '' ? {} : { comment }),
            });
          }}
        />
      )}
    </Card>
  );
}
